import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import WelcomeImage from '@/assets/images/onboarding/welcome.webm';
import FirstTimeExperienceTutorial from '../FirstTimeExperienceTutorial';
import { apiPostRequest } from '@/utils/apiClient';
import { getDeviceInfo } from '@/utils/deviceInfo';

/**
 * セッションIDを取得（存在しない場合は生成）
 */
const getOrCreateSessionId = (): string => {
    const storageKey = 'kotomil_session_id';
    let sessionId = sessionStorage.getItem(storageKey);
    if (!sessionId) {
        sessionId = crypto.randomUUID();
        sessionStorage.setItem(storageKey, sessionId);
    }
    return sessionId;
};

/**
 * イベントログを送信
 */
const logEvent = async (eventType: string, eventKey: string, metadata?: Record<string, unknown>) => {
    try {
        const sessionId = getOrCreateSessionId();
        
        await apiPostRequest('/api/log/event', {
            sessionId,
            eventType,
            eventKey,
            metadata,
        });
    } catch (error) {
        // ログ記録の失敗でユーザーに影響を与えないよう、静かに失敗
        console.error('イベントログ記録に失敗しました', error);
    }
};

interface OnboardingManagerProps {
    children: React.ReactNode;
}

const OnboardingManager: React.FC<OnboardingManagerProps> = ({ children }) => {
    const { t } = useTranslation('onboarding');
    
    // オンボーディング完了状態をチェック（セッション内のみ）
    const onboardingCompleted = sessionStorage.getItem('onboarding_completed') === 'true';
    
    // セッションストレージからチュートリアル開始状態を復元（ページ遷移後も状態を維持）
    const tutorialStarted = sessionStorage.getItem('tutorial_started') === 'true';
    // オンボーディングが完了している場合はモーダルとチュートリアルを表示しない
    const [showModal, setShowModal] = useState<boolean>(!onboardingCompleted && !tutorialStarted);
    const [showTutorial, setShowTutorial] = useState<boolean>(!onboardingCompleted && tutorialStarted);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const welcomePointsRaw = t('steps.welcome.points', { returnObjects: true }) as unknown;
    const welcomePoints = Array.isArray(welcomePointsRaw)
        ? (welcomePointsRaw as string[])
        : typeof welcomePointsRaw === 'string'
        ? [welcomePointsRaw]
        : [];

    // 動画の再生速度を設定
    useEffect(() => {
        videoRefs.current.forEach((video, index) => {
            if (video) {
                const rates = [1, 2, 1.5]; // 各動画の再生速度
                video.playbackRate = rates[index] || 1;
            }
        });
    }, [currentStep]);

    // モーダル表示時に背景のスクロールを無効化
    useEffect(() => {
        if (showModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // クリーンアップ
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showModal]);

    // オンボーディングのステップ定義
    const onboardingSteps = [
        {
            title: t('steps.welcome.title'),
            content: (
                <div className="space-y-6">
                    <video
                        ref={(el) => {
                            videoRefs.current[0] = el;
                        }}
                        src={WelcomeImage}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-auto"
                    />
                    <h2 className="text-xl font-bold text-center my-2 px-4">
                        {t('steps.welcome.heading')}
                    </h2>
                    <ul className="list-disc text-gray-600 space-y-3 px-8 leading-6">
                        {welcomePoints.map((point, index) => (
                            <li key={index}>
                                {point}
                            </li>
                        ))}
                    </ul>
                </div>
            ),
        },
    ];

    // 次のステップへ（現在は1ページのみなので完了へ）
    const nextStep = () => {
        // ウェルカムモーダルのイベントログを送信
        const deviceInfo = getDeviceInfo();
        logEvent('onboarding', 'modal_welcome', deviceInfo);
        
        startTutorial();
    };

    // チュートリアル開始（モーダルを閉じてチュートリアルを表示）
    const startTutorial = () => {
        // チュートリアルステップを初期化し、開始状態を保存
        sessionStorage.setItem('tutorial_global_step', '0');
        sessionStorage.setItem('tutorial_started', 'true');
        setShowModal(false);
        setShowTutorial(true);
    };

    // チュートリアル完了（すべて完了）
    const completeTutorial = () => {
        setShowTutorial(false);
        sessionStorage.removeItem('isNewUser');
        sessionStorage.removeItem('tutorial_global_step');
        sessionStorage.removeItem('tutorial_started');
        sessionStorage.setItem('onboarding_completed', 'true');
        // オンボーディング完了イベントを発火
        window.dispatchEvent(new CustomEvent('onboarding-completed'));
    };

    // モーダルの表示
    const renderModal = () => {
        if (!showModal) return null;

        const currentStepData = onboardingSteps[currentStep];
        if (!currentStepData) return null;

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                {/* 背景クリックでは閉じない */}
                <div className="absolute inset-0 bg-black bg-opacity-50" />

                <div className="relative bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] flex flex-col">
                    {/* スクロールエリア */}
                    <div
                        ref={scrollContainerRef}
                        className="flex-1 overflow-y-auto py-6 lg:px-6 relative"
                    >
                    {/* タイトル */}
                    <div className="px-6 pb-4">
                        <h1 className="text-2xl font-bold text-center text-gray-900">
                            {currentStepData.title}
                        </h1>
                    </div>
                        <div className="space-y-4 pb-10">{currentStepData.content}</div>
                    </div>
                    {/* 下部フェードアウト効果 - スクロールエリアの固定位置 */}
                    <div className="absolute bottom-24 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none"></div>

                    {/* ボタンと進捗表示を固定 */}
                    <div className="flex-shrink-0 p-6 pt-4">
                        {/* ボタンを中央寄せ */}
                        <div className="flex justify-center gap-4 mb-4">
                            <button
                                onClick={nextStep}
                                className="px-6 py-3 rounded hover:opacity-90 bg-kotomilThemaColor text-black font-bold"
                            >
                                {t('buttons.startTutorial', { defaultValue: 'チュートリアルを開始' })}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {children}
            {renderModal()}
            <FirstTimeExperienceTutorial
                isOpen={showTutorial}
                onClose={completeTutorial}
            />
        </>
    );
};

export default OnboardingManager;
